/**
 * Uniform-grid spatial hash over the XZ plane. Rebuilt every fixed step.
 * Zero-allocation queries: results are written into caller-supplied arrays.
 */
export class SpatialHash {
  constructor(cell = 4, worldHalf = 260) {
    this.cell = cell; this.inv = 1 / cell;
    this.half = worldHalf;
    this.dim = Math.ceil((worldHalf * 2) / cell) + 2;
    this.n = this.dim * this.dim;
    this.count = new Int32Array(this.n);
    this.start = new Int32Array(this.n + 1);
    this.items = new Int32Array(4096);
    this.cellOf = new Int32Array(4096);
    this._cap = 4096;
  }
  _grow(need) {
    if (need <= this._cap) return;
    let c = this._cap; while (c < need) c *= 2;
    this.items = new Int32Array(c); this.cellOf = new Int32Array(c); this._cap = c;
  }
  index(x, z) {
    let cx = ((x + this.half) * this.inv) | 0, cz = ((z + this.half) * this.inv) | 0;
    if (cx < 0) cx = 0; else if (cx >= this.dim) cx = this.dim - 1;
    if (cz < 0) cz = 0; else if (cz >= this.dim) cz = this.dim - 1;
    return cz * this.dim + cx;
  }
  /** Build from a list of live entity indices with xs/zs typed arrays. */
  build(ids, count, xs, zs) {
    this._grow(count);
    this.count.fill(0);
    const cellOf = this.cellOf;
    for (let i = 0; i < count; i++) {
      const id = ids[i];
      const c = this.index(xs[id], zs[id]);
      cellOf[i] = c; this.count[c]++;
    }
    let acc = 0;
    for (let c = 0; c < this.n; c++) { this.start[c] = acc; acc += this.count[c]; }
    this.start[this.n] = acc;
    const cursor = this._cursor || (this._cursor = new Int32Array(this.n));
    cursor.set(this.start.subarray(0, this.n));
    for (let i = 0; i < count; i++) this.items[cursor[cellOf[i]]++] = ids[i];
    this._len = count;
  }
  /** Append every id whose cell overlaps the circle (x,z,r) into `out`. Returns count. */
  query(x, z, r, out, max = 256) {
    let n = 0;
    const inv = this.inv, half = this.half, dim = this.dim;
    let x0 = (((x - r) + half) * inv) | 0, x1 = (((x + r) + half) * inv) | 0;
    let z0 = (((z - r) + half) * inv) | 0, z1 = (((z + r) + half) * inv) | 0;
    if (x0 < 0) x0 = 0; if (z0 < 0) z0 = 0;
    if (x1 >= dim) x1 = dim - 1; if (z1 >= dim) z1 = dim - 1;
    for (let cz = z0; cz <= z1; cz++) {
      const row = cz * dim;
      for (let cx = x0; cx <= x1; cx++) {
        const c = row + cx, s = this.start[c], e = this.start[c + 1];
        for (let i = s; i < e; i++) { if (n >= max) return n; out[n++] = this.items[i]; }
      }
    }
    return n;
  }
}
